import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ElementRef
} from '@angular/core';
import * as XLSX from 'xlsx';

export interface DataTableColumnConfig {
  header: string;
  field: string;
  orderable?: boolean;
  class?: string;
  dateFormat?: string; // if used with Date
  pipe?: 'date' | 'decimal' | 'number' | 'link' | null;
  // Optional numeric formatting controls (used when pipe === 'number')
  digitsInfo?: string; // e.g., '1.2-2', '1.0-0'
  locale?: string;     // e.g., 'en-US'
  template?: TemplateRef<any> | null;
  headerTemplate?: TemplateRef<any> | null;
}

@Component({
  selector: 'app-common-datatable',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent implements OnChanges {
  constructor(private cdr: ChangeDetectorRef) {
  }
  @ViewChild('internalTable', {static: false}) private internalTable?: ElementRef<HTMLTableElement>;
  @Input() tableId = 'common-table';
  @Input() data: any[] = [];
  @Input() columns: DataTableColumnConfig[] = [];

  // Paging state (kept external; parent owns paginate() logic)
  @Input() page = 1;
  @Output() pageChange = new EventEmitter<number>();
  @Input() pageSize = 20;
  @Output() pageSizeChange = new EventEmitter<number>();
  @Input() pageSizeOptions: number[] = [10, 20, 50, 100];
  @Input() totalRecords = 0;
  @Input() startIndex = 0;
  @Input() endIndex = 0;

  // Filtering
  @Input() showFilter = true;
  @Input() showPageSizeSelector = true;
  @Input() filterText = '';
  @Output() filterTextChange = new EventEmitter<string>();

  // Optional: mimic report look-and-feel (compact, hover, light header)
  @Input() reportView = false;

  // Export buttons (CSV / XLSX)
  @Input() showExport = true;
  @Input() exportFileName = 'export';

  // Row double-click
  @Output() rowDblClick = new EventEmitter<any>();

  // Cell link click
  @Output() cellLinkClick = new EventEmitter<any>();

  // sorting
  @Input() sortField: string | null = null;
  @Input() sortDirection: string | null = null;

  // Client-side sorting state
  // sortField: string | null = null;
  sortAsc = true;

  private _cachedRows: any[] = [];
  private _lastData: any[] | null = null;
  private _lastFilter = '';
  private _lastSortField: string | null = null;
  private _lastSortDir: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sortDirection'] && this.sortDirection != null) {
      this.sortAsc = this.sortDirection.toLowerCase() !== 'desc';
    }
    if (changes['data'] || changes['filterText'] || changes['sortField'] || changes['sortDirection']) {
      this._cachedRows = this._computeRows();
      this.cdr.markForCheck();
    }
  }

  // Derived view: apply filter and sort (does not mutate original data)
  get viewRows(): any[] {
    return this._cachedRows;
  }

  private _computeRows(): any[] {
    let rows = Array.isArray(this.data) ? [...this.data] : [];

    // filter
    const ft = (this.filterText || '').toString().toLowerCase().trim();
    if (ft) {
      rows = rows.filter(r => {
        return this.columns.some(col => {
          if (!col.field || col.field.startsWith('__')) {
            return false;
          }
          const v = this.getFieldValue(r, col.field);
          if (v === null || v === undefined) {
            return false;
          }
          let display: string;
          const s = String(v);
          // Always try to reformat ISO date strings (yyyy-MM-dd...) to MM/dd/yyyy
          // so date filtering works regardless of whether col.pipe === 'date' is set
          const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (isoMatch) {
            const mdy = `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
            return mdy.toLowerCase().includes(ft) || s.toLowerCase().includes(ft);
          } else {
            display = s;
          }
          return display.toLowerCase().includes(ft);
        });
      });
    }

    // sort
    if (this.sortField) {
      const field = this.sortField;
      const dir = this.sortAsc ? 1 : -1;
      rows.sort((a, b) => this.compareValues(this.getFieldValue(a, field), this.getFieldValue(b, field)) * dir);
    }

    return rows;
  }

  private compareValues(a: any, b: any): number {
    // Try to parse both values as dates (Date instances or date-like strings)
    const parseDate = (v: any): number | null => {
      if (v == null) {
        return null;
      }
      if (v instanceof Date) {
        const t = v.getTime();
        return isNaN(t) ? null : t;
      }
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) {
          return null;
        }
        // Common formats we support: ISO (yyyy-MM-dd or with time), yyyy/MM/dd, and MM/dd/yyyy
        const isoLike = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/;
        const ymdSlash = /^\d{4}\/\d{1,2}\/\d{1,2}$/;
        const mdy = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
        if (isoLike.test(s) || ymdSlash.test(s)) {
          const d = new Date(s);
          const t = d.getTime();
          return isNaN(t) ? null : t;
        }
        if (mdy.test(s)) {
          const parts = s.split('/');
          const m = parseInt(parts[0], 10);
          const d = parseInt(parts[1], 10);
          let y = parseInt(parts[2], 10);
          if (parts[2].length === 2) {
            y = 2000 + y;
          }
          const dt = new Date(y, m - 1, d);
          const t = dt.getTime();
          return isNaN(t) ? null : t;
        }
      }
      return null;
    };

    const at = parseDate(a);
    const bt = parseDate(b);
    // If either value looks like a date string, sort as date (nulls sort last)
    const eitherIsDate = at !== null || bt !== null;
    if (eitherIsDate) {
      if (at === null) {
        return 1;
      }
      if (bt === null) {
        return -1;
      }
      return at === bt ? 0 : at < bt ? -1 : 1;
    }

    // Handle numbers
    const an = typeof a === 'number' ? a : parseFloat(a);
    const bn = typeof b === 'number' ? b : parseFloat(b);
    if (!isNaN(an) && !isNaN(bn)) {
      return an === bn ? 0 : an < bn ? -1 : 1;
    }

    // Fallback to string compare
    const as = a === undefined || a === null ? '' : String(a).toLowerCase();
    const bs = b === undefined || b === null ? '' : String(b).toLowerCase();
    return as.localeCompare(bs);
  }

  onPageSizeChange(val: any): void {
    const size = Number(val);
    this.pageSizeChange.emit(size);
  }

  onPageChange(val: number): void {
    this.page = val;
    this.pageChange.emit(val);
  }

  onFilterInput(val: string): void {
    this.filterText = val;
    this.filterTextChange.emit(val);
    this._cachedRows = this._computeRows();
    this.cdr.markForCheck();
  }

  onRowDblClick(row: any): void {
    this.rowDblClick.emit(row);
  }

  onHeaderClick(col: DataTableColumnConfig): void {
    if (col.orderable === false) {
      return;
    }
    const field = col.field;
    if (!field || field.startsWith('__')) {
      return;
    }
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    this._cachedRows = this._computeRows();
    this.cdr.markForCheck();
  }

  // Expose the inner table element for consumers that need raw DOM (e.g., Excel export)
  public getTableElement(): HTMLTableElement | null {
    return this.internalTable?.nativeElement || null;
  }

  exportCsv(): void {
    const ws = XLSX.utils.json_to_sheet(this.getExportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.exportFileName + '.csv');
  }

  exportXlsx(): void {
    const ws = XLSX.utils.json_to_sheet(this.getExportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.exportFileName + '.xlsx');
  }

  private getExportRows(): any[] {
    return this.viewRows.map(row => {
      const out: any = {};
      this.columns.forEach(col => {
        if (!col.field || col.field.startsWith('__')) {
          return;
        }
        out[col.header || col.field] = this.getFieldValue(row, col.field);
      });
      return out;
    });
  }

  getFieldValue(row: any, field: string): any {
    if (!field) {
      return undefined;
    }
    return field.split('.').reduce((obj, key) => (obj != null ? obj[key] : undefined), row);
  }

  // Clear filter helper
  clearFilter(): void {
    this.filterText = '';
    this.filterTextChange.emit('');
    this._cachedRows = this._computeRows();
    this.cdr.markForCheck();
  }
}
